import React, { useState, useEffect, useCallback, useMemo } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Card,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import {
  Button,
  Block,
  BlockHead,
  BlockTitle,
  Icon,
  Col,
  Row,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
} from "../../../components/Component";
import { useHistory, useParams } from "react-router-dom";
import http from "../../../utils/http-common";
import swal from "sweetalert";
import moment from "moment";
import ConfirmModal from "../../../components/partials/user-manage/ConfirmModal";
import UserPicker from "../../../components/partials/user-manage/UserPicker";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { getPublicUrl, API_BASE_URL } from "../../../utils/Utils";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import {
  userDirectoryLoginAllowed,
  userAccountStatusLabel,
  userAccountStatusBadgeVariant,
} from "../../../utils/userAccountStatus";
import PartnerStatusBadge from "../../../components/features/medical-partner-admin/PartnerStatusBadge";
import LicenseEditors from "../../../components/licenses/LicenseEditors";
import PartnerTeleconsultationTab from "../../../components/features/partner-teleconsultation/PartnerTeleconsultationTab";
import PartnerDetailTabNav from "./medical-partner-details/PartnerDetailTabNav";
import PartnerReviewAlerts from "./medical-partner-details/PartnerReviewAlerts";
import PartnerDetailPageHeader from "./medical-partner-details/PartnerDetailPageHeader";
import PrimaryUserGateNotice from "./medical-partner-details/PrimaryUserGateNotice";
import { DEFAULT_PATIENT_WEB_ORIGIN } from "./medical-partner-details/constants";
import { userRoleBadgeClass, practitionerListingBadge } from "./medical-partner-details/helpers";
import EditMedicalPartnerModal from "../../../components/features/medical-partner-admin/EditMedicalPartnerModal";
import PartnerDetailsOverviewTab from "./medical-partner-details/PartnerDetailsOverviewTab";
import PartnerDetailsClinicsTab from "./medical-partner-details/PartnerDetailsClinicsTab";
import { partnerOffersMedicalTourism } from "../../../components/features/medical-partner-admin/medicalPartnerAdminConstants";
import { partnerWorkflowStatus } from "../../../components/features/medical-partner-admin/medicalPartnerListTableHelpers";
import { runPartnerWorkflowTransition } from "../../../components/features/medical-partner-admin/partnerWorkflowActions";
import { usePartnerEditLock } from "../../../hooks/usePartnerEditLock";
import { medicalPartnerPutConfig } from "../../../utils/partnerEditLockRequest";

function resolveBackendAssetUrl(relativeOrAbsolute) {
  const u = String(relativeOrAbsolute || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const base = String(API_BASE_URL || "").replace(/\/$/, "");
  const pathPart = u.startsWith("/") ? u : `/${u}`;
  return `${base}${pathPart}`;
}

/** Pure helpers keep eslint-plugin-react-hooks code-path analysis stable (avoid && / ternary between hook calls). */
function medicalPartnerLockPartnerId(viewerIsAdmin, routeOrDetailId) {
  if (!viewerIsAdmin || !routeOrDetailId) return null;
  return String(routeOrDetailId);
}

function medicalPartnerLockEnabled(viewerIsAdmin, partnerRow, isLoading) {
  return Boolean(viewerIsAdmin && partnerRow && !isLoading);
}

function medicalPartnerDossierEditBlocked(viewerIsAdmin, lockedByPeer, sessionExpired) {
  return Boolean(viewerIsAdmin && (lockedByPeer || sessionExpired));
}

function MedicalPartnerDetails(props = {}) {
  const {
  detailPartnerId = null,
  onClosePanel = null,
  refetchPartners = null,
  } = props;
  const params = useParams();
  const id = detailPartnerId || params?.id || null;
  const inPanel = Boolean(detailPartnerId);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [user, setUser] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [totalClinics, setTotalClinics] = useState(0);
  const [clinicPage, setClinicPage] = useState(1);
  const clinicLimit = 10;
  const [activeTab, setActiveTab] = useState("overview");
  const [showConfirmModal, setShowConfirmModal] = useState({
    isOpen: false,
    type: null,
    clinic: null,
    onConfirm: null,
  });
  const [showClinicDetailsModal, setShowClinicDetailsModal] = useState({
    isOpen: false,
    clinic: null,
  });
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugValue, setSlugValue] = useState("");
  const [savingSlug, setSavingSlug] = useState(false);
  const [practitioners, setPractitioners] = useState([]);
  /** Controlled drafts for practitioner compliance fields (synced from API on each practitioners refresh). */
  const [practitionerFieldDrafts, setPractitionerFieldDrafts] = useState({});
  const [practitionerLinkSelect, setPractitionerLinkSelect] = useState({});
  const [clinicsSummary, setClinicsSummary] = useState([]);
  const [tcSaving, setTcSaving] = useState(false);
  const [mtProgramSaving, setMtProgramSaving] = useState(false);
  const [clinicTcChannelBusyId, setClinicTcChannelBusyId] = useState(null);
  const [practitionerBusyId, setPractitionerBusyId] = useState(null);
  const [selectedCoManagerUser, setSelectedCoManagerUser] = useState(null);
  const [coManagerBusy, setCoManagerBusy] = useState(false);
  const [selectedPrimaryLinkUser, setSelectedPrimaryLinkUser] = useState(null);
  const [primaryLinkBusy, setPrimaryLinkBusy] = useState(false);
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [inlineFieldBusy, setInlineFieldBusy] = useState(null);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [enableUserLoginBusy, setEnableUserLoginBusy] = useState(false);

  const lockPartnerId = medicalPartnerLockPartnerId(viewerIsAdmin, id);
  const lockEnabled = medicalPartnerLockEnabled(viewerIsAdmin, partner, loading);
  const {
    editLockToken,
    partnerDossierLockedByPeer,
    lockHolderUserId,
    lockSessionExpired,
    takeoverEditLock,
  } = usePartnerEditLock(lockPartnerId, lockEnabled);

  const dossierEditBlocked = medicalPartnerDossierEditBlocked(
    viewerIsAdmin,
    partnerDossierLockedByPeer,
    lockSessionExpired
  );

  useEffect(() => {
    const ac = new AbortController();
    http
      .get("/api/v1/user/me", { signal: ac.signal })
      .then((r) => {
        const u = r.data?.data || r.data;
        setViewerIsAdmin(String(u?.role || "").toLowerCase() === "admin");
      })
      .catch(() => setViewerIsAdmin(false));
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const next = {};
    practitioners.forEach((pr) => {
      next[String(pr._id)] = {
        practiceCountry: (pr.practiceCountry || "").trim().toUpperCase(),
        professionalId: (pr.professionalId || "").trim(),
        medicalLicenceNumber: (pr.medicalLicenceNumber || "").trim(),
        diplomas: Array.isArray(pr.diplomas) ? pr.diplomas.join(", ") : "",
        credentialDiplomas: (pr.credentialDiplomas || "").trim(),
      };
    });
    setPractitionerFieldDrafts(next);
  }, [practitioners]);

  const publicWebBase = useMemo(() => {
    const raw = (process.env.REACT_APP_PUBLIC_WEB_URL || "").trim();
    const base = raw || DEFAULT_PATIENT_WEB_ORIGIN;
    return String(base).replace(/\/$/, "");
  }, []);

  const partnerPublicPageUrl = useMemo(() => {
    const slug = partner?.landing_slug || partner?.slug || "";
    if (!slug || !publicWebBase) return "";
    return `${publicWebBase}/partners/${slug}`;
  }, [partner?.landing_slug, partner?.slug, publicWebBase]);

  /** Overview metrics must use full clinic list (`clinicsSummary`), not the paginated `clinics` slice (e.g. 10/page). */
  const clinicMetrics = useMemo(() => {
    const source =
      Array.isArray(clinicsSummary) && clinicsSummary.length > 0 ? clinicsSummary : clinics;
    const active = source.filter((c) => c.status === "active" || c.status === "Active").length;
    const pending = source.filter((c) => c.status === "pending" || c.status === "Pending").length;
    const defaultClinics = source.filter((c) => c.isDefault).length;
    return { active, pending, defaultClinics, total: source.length };
  }, [clinics, clinicsSummary]);

  /** `true` = directory user can log in; `false` = disabled; `null` = user not yet loaded. */
  const primaryUserLoginActive = useMemo(() => {
    if (!partner?.userId || user == null) return null;
    return userDirectoryLoginAllowed(user);
  }, [partner?.userId, user]);

  /** Merged clinic list for assistant-friendly shortcuts to per-clinic practitioner admin. */
  const clinicsForPractitionerAdminLinks = useMemo(() => {
    const byId = new Map();
    const ingest = (arr) => {
      (arr || []).forEach((c) => {
        const id = c?._id;
        if (id && !byId.has(String(id))) byId.set(String(id), c);
      });
    };
    ingest(clinicsSummary);
    ingest(clinics);
    return Array.from(byId.values());
  }, [clinicsSummary, clinics]);

  const filterCoManagerCandidate = useCallback(
    (u) => {
      if (!u) return false;
      const mp = u.medicalPartnerId;
      if (!mp) return true;
      const mpId = typeof mp === "object" && mp ? mp._id : mp;
      return String(mpId) === String(partner?._id);
    },
    [partner?._id]
  );

  const filterPrimaryLinkCandidate = useCallback(
    (u) => {
      if (!u) return false;
      const mp = u.medicalPartnerId;
      if (!mp) return true;
      const mpId = typeof mp === "object" && mp ? mp._id : mp;
      return String(mpId) === String(partner?._id);
    },
    [partner?._id]
  );

  useEffect(() => {
    const primaryUserIdStr = partner?.userId ? String(partner.userId._id || partner.userId) : "";
    const hasPU = Boolean(primaryUserIdStr);
    if (!hasPU && activeTab !== "overview") {
      setActiveTab("overview");
    }
  }, [partner, activeTab]);

  useEffect(() => {
    if (!id) return undefined;
    const ac = new AbortController();
    const { signal } = ac;

    const fetchPartnerData = async () => {
      setLoading(true);
      try {
        const partnersResponse = await http.get(`/api/v1/medical-partners/${id}`, {
          params: { include: "practitioners" },
          signal,
        });
        if (signal.aborted) return;
        const partnerData = partnersResponse.data?.data || partnersResponse.data;

        if (!partnerData) {
          swal("Error", "Partner not found", "error");
          setLoading(false);
          return;
        }

        setPartner(partnerData);
        setPractitioners(Array.isArray(partnersResponse.data?.practitioners) ? partnersResponse.data.practitioners : []);
        setClinicsSummary(Array.isArray(partnersResponse.data?.clinicsSummary) ? partnersResponse.data.clinicsSummary : []);

        const userId = partnerData.userId?._id || partnerData.userId;
        if (userId) {
          try {
            const userResponse = await http.get(`/api/v1/user/${userId}`, { signal });
            if (signal.aborted) return;
            const userData = userResponse.data?.data || userResponse.data;
            setUser(userData);
          } catch (error) {
            if (!isAbortError(error)) console.error("Error loading user:", error);
          }
        }

        try {
          const clinicsResponse = await http.get(`/api/v1/partners/medical/clinics`, {
            params: { partnerId: partnerData._id, page: 1, limit: clinicLimit },
            signal,
          });
          if (signal.aborted) return;
          const clinicsData = clinicsResponse.data?.data || clinicsResponse.data || [];
          setClinics(Array.isArray(clinicsData) ? clinicsData : []);
          setTotalClinics(clinicsResponse.data?.total ?? clinicsResponse.data?.meta?.total ?? clinicsData.length);
          setClinicPage(1);
        } catch (error) {
          if (!isAbortError(error)) console.error("Error loading clinics:", error);
          if (!signal.aborted) {
            setClinics([]);
            setTotalClinics(0);
          }
        }
      } catch (error) {
        if (isAbortError(error) || signal.aborted) return;
        console.error("Error loading partner:", error);
        swal("Error", error.response?.data?.message || "Unable to load partner data", "error");
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchPartnerData();
    return () => ac.abort();
  }, [id]);

  const openEditProfileModal = useCallback(() => {
    if (!partner) return;
    setEditProfileModalOpen(true);
  }, [partner]);

  const closeEditProfileModal = useCallback(() => {
    setEditProfileModalOpen(false);
  }, []);

  const refreshPartnerDetailBundle = useCallback(async () => {
    if (!id) return;
    const res = await http.get(`/api/v1/medical-partners/${id}`, { params: { include: "practitioners" } });
    const refreshed = res.data?.data || res.data;
    if (refreshed) setPartner(refreshed);
    setPractitioners(Array.isArray(res.data?.practitioners) ? res.data.practitioners : []);
    setClinicsSummary(Array.isArray(res.data?.clinicsSummary) ? res.data.clinicsSummary : []);

    const userId = refreshed?.userId?._id || refreshed?.userId;
    if (userId) {
      try {
        const userResponse = await http.get(`/api/v1/user/${userId}`);
        const userData = userResponse.data?.data || userResponse.data;
        setUser(userData);
      } catch (error) {
        if (!isAbortError(error)) console.error("Error refreshing linked user:", error);
      }
    }
  }, [id]);

  const patchPartnerField = useCallback(
    async (fieldKey, nextString) => {
      if (!viewerIsAdmin || !id) return;
      if (dossierEditBlocked) {
        swal(
          "Read-only",
          "Another admin is editing this dossier, or the edit session expired. Take over the lock from the banner if appropriate.",
          "info"
        );
        return;
      }
      if (fieldKey === "contact_person_email") {
        swal(
          "Read-only",
          "Login and notifications use the primary directory user email. Open the linked user to change it.",
          "info"
        );
        return;
      }
      const payload = { [fieldKey]: String(nextString).trim() === "" ? null : String(nextString).trim() };
      setInlineFieldBusy(String(fieldKey));
      try {
        const res = await http.put(
          `/api/v1/medical-partners/${id}`,
          payload,
          medicalPartnerPutConfig(editLockToken, payload)
        );
        const next = res.data?.data || res.data;
        if (next) setPartner((prev) => (prev ? { ...prev, ...next } : next));
        refetchPartners?.();
      } catch (e) {
        swal("Error", e.response?.data?.message || "Update failed", "error");
        throw e;
      } finally {
        setInlineFieldBusy(null);
      }
    },
    [viewerIsAdmin, id, refetchPartners, editLockToken, dossierEditBlocked]
  );

  const enableUserLogin = useCallback(
    async (silent = false) => {
      const uid = partner?.userId ? String(partner.userId._id || partner.userId) : "";
      if (!uid) return false;
      try {
        const res = await http.put(`/api/v1/user/${uid}`, {
          status: "active",
          active: true,
          banReason: null,
        });
        const updated = res.data?.data || res.data;
        setUser(updated);
        return true;
      } catch (e) {
        if (!silent) {
          swal("Error", e?.response?.data?.message || e?.message || "Could not enable login", "error");
        }
        return false;
      }
    },
    [partner, setUser]
  );

  const onEnableUserLogin = useCallback(async () => {
    if (!viewerIsAdmin || primaryUserLoginActive !== false) return;
    const email = user?.email || "";
    const ok = await swal({
      title: "Enable user login?",
      text: `Allow ${email || "this user"} to log in again?`,
      icon: "info",
      buttons: ["Cancel", "Enable login"],
    });
    if (!ok) return;
    setEnableUserLoginBusy(true);
    try {
      const done = await enableUserLogin(false);
      if (done) swal("Done", "Login enabled.", "success");
    } finally {
      setEnableUserLoginBusy(false);
    }
  }, [viewerIsAdmin, primaryUserLoginActive, user, enableUserLogin]);

  const onPartnerWorkflowStatusChange = useCallback(
    async (nextStatus) => {
      if (!viewerIsAdmin || !partner?._id) return;
      const current = partnerWorkflowStatus(partner);
      if (current === nextStatus) return;

      if (nextStatus === "active" && primaryUserLoginActive === false) {
        const choice = await swal({
          title: "Set partner to Live?",
          text: "User login is currently disabled. Enable it along with Go Live?",
          icon: "warning",
          buttons: {
            cancel: "Cancel",
            liveOnly: { text: "Set Live only", value: "liveOnly" },
            both: { text: "Set Live + Enable login", value: "both", className: "btn-primary" },
          },
        });
        if (!choice) return;

        setWorkflowBusy(true);
        try {
          const applied = await runPartnerWorkflowTransition(partner, nextStatus, { http, swal });
          if (!applied) { setWorkflowBusy(false); return; }
          if (choice === "both") await enableUserLogin(true);
          await refreshPartnerDetailBundle();
          refetchPartners?.();
          swal("Success", choice === "both" ? "Partner Live + login enabled." : "Partner set to Live.", "success");
        } catch (e) {
          swal("Error", e?.response?.data?.message || e?.message || "Update failed", "error");
        } finally {
          setWorkflowBusy(false);
        }
        return;
      }

      setWorkflowBusy(true);
      try {
        const applied = await runPartnerWorkflowTransition(partner, nextStatus, { http, swal });
        if (applied) {
          await refreshPartnerDetailBundle();
          refetchPartners?.();
          const msg =
            nextStatus === "active" &&
            (current === "pending" || current === "inactive" || current === "draft")
              ? "Partner set to Live"
              : "Partner status updated";
          swal("Success", msg, "success");
        }
      } catch (e) {
        swal("Error", e?.response?.data?.message || e?.message || "Update failed", "error");
      } finally {
        setWorkflowBusy(false);
      }
    },
    [viewerIsAdmin, partner, primaryUserLoginActive, enableUserLogin, refreshPartnerDetailBundle, refetchPartners]
  );

    const loadingInner = (
      <Block>
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Block>
    );

    const notFoundInner = (
      <Block>
        <div className="text-center p-5">
          <h4>Medical partner not found</h4>
          {inPanel && typeof onClosePanel === "function" ? (
            <Button type="button" color="primary" className="mt-2" onClick={onClosePanel}>
              Back to list
            </Button>
          ) : (
            <Button type="button" className="mt-2" onClick={() => history.goBack()}>
              Back
            </Button>
          )}
        </div>
      </Block>
    );

  const primaryUserIdStr = partner?.userId ? String(partner.userId._id || partner.userId) : "";
  const hasPrimaryUser = Boolean(primaryUserIdStr);
  const partnerSlugForUrl = partner?.landing_slug || partner?.slug || "";

  const onEditProfileSaved = async (savedId) => {
    refetchPartners?.();
    if (!id || !savedId || String(savedId) !== String(id)) return;
    try {
      const res = await http.get(`/api/v1/medical-partners/${id}`, { params: { include: "practitioners" } });
      const refreshed = res.data?.data || res.data;
      setPartner(refreshed);
      setPractitioners(Array.isArray(res.data?.practitioners) ? res.data.practitioners : []);
      setClinicsSummary(Array.isArray(res.data?.clinicsSummary) ? res.data.clinicsSummary : []);
      const uid = refreshed?.userId?._id || refreshed?.userId;
      if (uid) {
        try {
          const userResponse = await http.get(`/api/v1/user/${uid}`);
          setUser(userResponse.data?.data || userResponse.data);
        } catch (userErr) {
          if (process.env.NODE_ENV === "development") console.error(userErr);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error(e);
      swal("Error", e.response?.data?.message || "Refresh failed", "error");
    }
  };

  const refreshPractitioners = async () => {
    if (!id) return;
    try {
      const res = await http.get(`/api/v1/medical-partners/${id}`, { params: { include: "practitioners" } });
      const refreshed = res.data?.data || res.data;
      setPartner(refreshed);
      setPractitioners(Array.isArray(res.data?.practitioners) ? res.data.practitioners : []);
      setClinicsSummary(Array.isArray(res.data?.clinicsSummary) ? res.data.clinicsSummary : []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error(e);
      }
    }
  };

  const isStaffRole = (role) => ["nurse", "assistant"].includes(String(role || "").toLowerCase());

  const openCatalogueGolive = () => {
    history.push(getPublicUrl("teleconsultations/practitioner-approvals"));
  };

  const renderListingStatus = (p) => {
    const badge = practitionerListingBadge(p.platformListingDecision);
    const decision = String(p.platformListingDecision || "pending").toLowerCase();
    let hint = "Awaiting Catalogue go-live";
    if (decision === "approved" && p.teleconsultationAuthorized) {
      hint = "Listed on public catalog";
    } else if (decision === "approved") {
      hint = "Approved — complete TC setup in Catalogue go-live if needed";
    } else if (decision === "rejected") {
      hint = "Not on public catalog";
    }
    return (
      <div className="d-flex flex-column align-items-start" style={{ minWidth: 108 }}>
        <span className={`badge badge-pill ${badge.className}`}>{badge.label}</span>
        <span className="text-soft small mt-1">{hint}</span>
      </div>
    );
  };

  const updatePractitionerCompliance = async (row, payload) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id) return;
    setPractitionerBusyId(String(row._id));
    try {
      await http.put(`/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}`, payload, {
        suppressInlineErrorSwal: true,
      });
      await refreshPractitioners();
    } catch (e) {
      swal("Practitioner could not be updated", formatAdminApiError(e, "Update failed"), "error");
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const uploadLicenceDocument = async (row, file) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id || !file) return;
    setPractitionerBusyId(String(row._id));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await http.post(
        `/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}/licence-document`,
        fd,
        { suppressInlineErrorSwal: true }
      );
      await refreshPractitioners();
      swal("Success", "Licence document uploaded.", "success");
    } catch (e) {
      swal("Upload failed", formatAdminApiError(e, "Upload failed"), "error");
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const deleteLicenceDocument = async (row) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id) return;
    setPractitionerBusyId(String(row._id));
    try {
      await http.delete(`/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}/licence-document`, {
        suppressInlineErrorSwal: true,
      });
      await refreshPractitioners();
      swal("Success", "Licence document removed.", "success");
    } catch (e) {
      swal("Could not remove document", formatAdminApiError(e, "Remove failed"), "error");
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const removePractitionerFromClinic = (row) => {
    const cid = row.clinicId || row.clinic;
    if (!cid || !row._id) return;
    swal({
      title: "Remove from clinic?",
      text: "This removes the practitioner row for this clinic only.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (!willDelete) return;
      setPractitionerBusyId(String(row._id));
      http
        .delete(`/api/v1/partners/medical/clinics/${cid}/practitioners/${row._id}`, {
          suppressInlineErrorSwal: true,
        })
        .then(() => refreshPractitioners())
        .then(() => swal("Done", "Practitioner removed from clinic", "success"))
        .catch((e) => swal("Could not remove practitioner", formatAdminApiError(e, "Remove failed"), "error"))
        .finally(() => setPractitionerBusyId(null));
    });
  };

  const coManagerIdsNormalized = () => {
    const raw = partner?.coManagers || [];
    return raw
      .map((cm) => {
        if (cm && typeof cm === "object" && cm._id) return String(cm._id);
        if (cm) return String(cm);
        return null;
      })
      .filter(Boolean);
  };

  const addCoManager = async () => {
    const raw = selectedCoManagerUser?._id ? String(selectedCoManagerUser._id) : "";
    if (!partner?._id || !raw) {
      swal("Validation", "Search and select a user to add as co-manager.", "warning");
      return;
    }
    const primaryId = String(partner.userId?._id || partner.userId || "");
    if (primaryId && primaryId === raw) {
      swal("Validation", "This user is already the primary partner account.", "warning");
      return;
    }
    const existing = coManagerIdsNormalized();
    if (existing.includes(raw)) {
      swal("Validation", "This user is already a co-manager.", "info");
      return;
    }
    if (viewerIsAdmin && dossierEditBlocked) {
      swal("Read-only", "Another admin holds the edit lock for this dossier.", "info");
      return;
    }
    setCoManagerBusy(true);
    try {
      const cmPayload = { coManagers: [...existing, raw] };
      await http.put(
        `/api/v1/medical-partners/${partner._id}`,
        cmPayload,
        medicalPartnerPutConfig(editLockToken, cmPayload)
      );
      setSelectedCoManagerUser(null);
      await refreshPractitioners();
      refetchPartners?.();
      swal("Done", "Co-manager added.", "success");
    } catch (e) {
      swal("Error", e.response?.data?.message || "Failed to add co-manager", "error");
    } finally {
      setCoManagerBusy(false);
    }
  };

  const removeCoManager = async (cm) => {
    const idStr = String(cm?._id || cm);
    const next = coManagerIdsNormalized().filter((x) => x !== idStr);
    const label =
      typeof cm === "object" && cm != null
        ? [cm.first_name, cm.last_name].filter(Boolean).join(" ").trim() || cm.email || idStr
        : idStr;
    const ok = await swal({
      title: "Remove co-manager?",
      text: `Remove ${label} from co-managers? They will lose shared dashboard access for this partner.`,
      icon: "warning",
      buttons: ["Cancel", "Remove"],
      dangerMode: true,
    });
    if (!ok) return;
    if (viewerIsAdmin && dossierEditBlocked) {
      swal("Read-only", "Another admin holds the edit lock for this dossier.", "info");
      return;
    }
    setCoManagerBusy(true);
    try {
      const cmPayload = { coManagers: next };
      await http.put(
        `/api/v1/medical-partners/${partner._id}`,
        cmPayload,
        medicalPartnerPutConfig(editLockToken, cmPayload)
      );
      await refreshPractitioners();
      refetchPartners?.();
      swal("Done", "Co-manager removed.", "success");
    } catch (e) {
      swal("Error", e.response?.data?.message || "Failed to remove", "error");
    } finally {
      setCoManagerBusy(false);
    }
  };

  const linkOrReplacePrimaryUser = async ({ replace }) => {
    const sel = selectedPrimaryLinkUser;
    if (!partner?._id || !sel?._id) {
      swal("Validation", "Search and select a user from the directory.", "warning");
      return;
    }
    const uid = String(sel._id);
    if (!replace && primaryUserIdStr && uid === primaryUserIdStr) {
      swal("Info", "This user is already the primary account.", "info");
      return;
    }
    if (replace && primaryUserIdStr && uid === primaryUserIdStr) {
      swal("Info", "Pick a different user than the current primary account.", "info");
      return;
    }
    if (replace) {
      const ok = await swal({
        title: "Change primary account?",
        text: "The previous primary user loses partner ownership on their profile unless they remain a co-manager. Continue?",
        icon: "warning",
        buttons: ["Cancel", "Change primary"],
        dangerMode: true,
      });
      if (!ok) return;
    }
    if (viewerIsAdmin && dossierEditBlocked) {
      swal("Read-only", "Another admin holds the edit lock for this dossier.", "info");
      return;
    }
    setPrimaryLinkBusy(true);
    try {
      const uidPayload = { userId: uid };
      await http.put(
        `/api/v1/medical-partners/${partner._id}`,
        uidPayload,
        medicalPartnerPutConfig(editLockToken, uidPayload)
      );
      setSelectedPrimaryLinkUser(null);
      await onEditProfileSaved(String(partner._id));
      refetchPartners?.();
      swal("Done", replace ? "Primary account updated." : "Primary account linked.", "success");
    } catch (e) {
      swal("Error", formatAdminApiError(e, "Update failed"), "error");
    } finally {
      setPrimaryLinkBusy(false);
    }
  };

  const linkPractitionerToClinic = async (row, targetClinicId) => {
    const uid = row.user?._id || row.user;
    if (!targetClinicId || !uid) {
      swal("Error", "A linked user account is required to add this practitioner to another clinic.", "error");
      return;
    }
    setPractitionerBusyId(String(row._id));
    try {
      await http.post(
        `/api/v1/partners/medical/clinics/${targetClinicId}/practitioners`,
        {
          user: uid,
          role: row.role || "doctor",
          teleconsultationAuthorized: false,
        },
        { suppressInlineErrorSwal: true }
      );
      await refreshPractitioners();
      swal("Success", "Practitioner linked to clinic", "success");
    } catch (e) {
      swal("Could not link practitioner to clinic", formatAdminApiError(e, "Link failed"), "error");
    } finally {
      setPractitionerBusyId(null);
    }
  };

  const fetchClinicsPage = async (page) => {
    if (!partner?._id) return;
    try {
      const clinicsResponse = await http.get(`/api/v1/partners/medical/clinics`, {
        params: { partnerId: partner._id, page, limit: clinicLimit }
      });
      const clinicsData = clinicsResponse.data?.data || clinicsResponse.data || [];
      setClinics(Array.isArray(clinicsData) ? clinicsData : []);
      setTotalClinics(clinicsResponse.data?.total ?? clinicsResponse.data?.meta?.total ?? 0);
      setClinicPage(page);
    } catch (error) {
      console.error("Error loading clinics page:", error);
      setClinics([]);
    }
  };

  const updateClinicStatus = async (clinicId, status, reason = "") => {
    try {
      await http.put(`/api/v1/partners/medical/clinics/${clinicId}`, {
        status,
        rejectionReason: reason || undefined,
      });
      
      const clinicsResponse = await http.get(`/api/v1/partners/medical/clinics`, {
        params: { partnerId: partner._id, page: clinicPage, limit: clinicLimit }
      });
      const clinicsData = clinicsResponse.data?.data || clinicsResponse.data || [];
      setClinics(Array.isArray(clinicsData) ? clinicsData : []);
      setTotalClinics(clinicsResponse.data?.total ?? clinicsResponse.data?.meta?.total ?? totalClinics);
      
      swal("Success", `Clinic ${status === "active" ? "approved" : "rejected"}`, "success");
    } catch (error) {
      console.error("Error updating clinic:", error);
      swal("Error", error.response?.data?.message || "Update failed", "error");
    }
  };

  const savePartnerTc = async (nextStatus, note = "") => {
    if (!partner?._id) return;
    setTcSaving(true);
    try {
      await http.put(`/api/v1/medical-partners/${partner._id}`, {
        tcApprovalStatus: nextStatus,
        tcApprovalNote: note,
        tcApprovalUpdatedAt: new Date().toISOString(),
      });
      await refreshPartnerDetailBundle();
      refetchPartners?.();
    } catch (e) {
      swal("Error", e.response?.data?.message || "Update failed", "error");
    } finally {
      setTcSaving(false);
    }
  };

  const toggleMedicalTourismProgram = async (wantOn) => {
    if (!partner?._id) return;
    if (partnerOffersMedicalTourism(partner) === wantOn) return;
    if (viewerIsAdmin && dossierEditBlocked) {
      swal("Read-only", "Another admin holds the edit lock for this dossier.", "info");
      return;
    }
    setMtProgramSaving(true);
    try {
      const mtPayload = { offersMedicalTourism: wantOn };
      await http.put(
        `/api/v1/medical-partners/${partner._id}`,
        mtPayload,
        medicalPartnerPutConfig(editLockToken, mtPayload)
      );
      await refreshPartnerDetailBundle();
      refetchPartners?.();
    } catch (e) {
      swal("Error", e.response?.data?.message || "Update failed", "error");
    } finally {
      setMtProgramSaving(false);
    }
  };

  const updateClinicTeleconsultationChannel = async (clinicId, patch) => {
    if (!clinicId || !partner?._id) return;
    setClinicTcChannelBusyId(String(clinicId));
    try {
      await http.put(`/api/v1/partners/medical/clinics/${clinicId}`, patch);
      await refreshPartnerDetailBundle();
      refetchPartners?.();
    } catch (e) {
      swal("Error", e.response?.data?.message || "Could not update clinic", "error");
    } finally {
      setClinicTcChannelBusyId(null);
    }
  };

  const patchClinicOffersAndRefreshList = async (clinicId, patch) => {
    if (!clinicId) return;
    await updateClinicTeleconsultationChannel(clinicId, patch);
    if (partner?._id) {
      await fetchClinicsPage(clinicPage);
    }
  };

  const confirmUpdateClinic = (clinic, status) => {
    setShowConfirmModal({
      isOpen: true,
      type: status === "active" ? "approveClinic" : "rejectClinic",
      clinic,
      onConfirm: () => {
        updateClinicStatus(clinic._id, status);
        setShowConfirmModal({ isOpen: false, type: null, clinic: null, onConfirm: null });
      },
    });
  };

  const layoutBody =
    !loading && partner ? (
    <>
        <PartnerDetailPageHeader
          inPanel={inPanel}
          organizationName={partner.organization_name}
          partner={partner}
          user={user}
          canEditProfile={viewerIsAdmin && !dossierEditBlocked}
          onBreadcrumbPartners={() => {
            if (inPanel && typeof onClosePanel === "function") onClosePanel();
            else history.push(getPublicUrl("medical-partner"));
          }}
          onEditProfile={openEditProfileModal}
          onCloseOrBack={() => {
            if (inPanel && typeof onClosePanel === "function") onClosePanel();
            else history.goBack();
          }}
        />
        <PartnerReviewAlerts partner={partner} user={user} />

        {viewerIsAdmin && partnerDossierLockedByPeer ? (
          <div
            className="alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
            role="region"
            aria-label="Partner dossier locked"
          >
            <span className="mb-0 small">
              Another admin is editing this partner dossier
              {lockHolderUserId ? (
                <>
                  {" "}
                  (session <span className="text-base font-monospace">{String(lockHolderUserId)}</span>)
                </>
              ) : null}
              . You can take over the lock if you need to continue here.
                              </span>
                                  <Button
              color="warning"
                                    size="sm"
                                    type="button"
              className="text-dark"
                                    onClick={async () => {
                const ok = await takeoverEditLock();
                if (ok) swal("Lock acquired", "You can edit this dossier on this tab.", "success");
                else swal("Could not take over", "Try again or reload the page.", "error");
              }}
            >
              Take over editing
                                  </Button>
                                </div>
        ) : null}

        {viewerIsAdmin && lockSessionExpired ? (
          <div className="alert alert-danger d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <span className="mb-0 small">Your edit session expired or was invalidated. Reload to start a new session.</span>
            <Button color="danger" size="sm" outline type="button" onClick={() => window.location.reload()}>
              Reload
                                  </Button>
                                </div>
        ) : null}

                      <Block>
          <Card className="card-bordered">
            <PartnerDetailTabNav
              activeTab={activeTab}
              onTabChange={setActiveTab}
              hasPrimaryUser={hasPrimaryUser}
              totalClinics={totalClinics}
            />

                        <div className="card-inner">
              {!hasPrimaryUser ? <PrimaryUserGateNotice /> : null}
              {activeTab === "overview" && (
                <PartnerDetailsOverviewTab
                  partner={partner}
                  hasPrimaryUser={hasPrimaryUser}
                  onOpenTeleconsultationTab={() => hasPrimaryUser && setActiveTab("teleconsultation")}
                  onOpenClinicsTab={() => hasPrimaryUser && setActiveTab("clinics")}
                  inlineEdit={viewerIsAdmin && !dossierEditBlocked}
                  inlineFieldBusy={inlineFieldBusy}
                  onPatchPartnerField={patchPartnerField}
                  editingSlug={editingSlug}
                  setEditingSlug={setEditingSlug}
                  slugValue={slugValue}
                  setSlugValue={setSlugValue}
                  savingSlug={savingSlug}
                  setSavingSlug={setSavingSlug}
                  partnerSlugForUrl={partnerSlugForUrl}
                  partnerPublicPageUrl={partnerPublicPageUrl}
                  primaryDirectoryAccountEmail={
                    (user?.email && String(user.email).trim()) ||
                    (partner?.userId && typeof partner.userId === "object" && partner.userId.email
                      ? String(partner.userId.email).trim()
                      : "")
                  }
                  clinicMetrics={clinicMetrics}
                  totalClinics={totalClinics}
                  setPartner={setPartner}
                  refetchPartners={refetchPartners}
                  tcSaving={tcSaving}
                  onSavePartnerTc={savePartnerTc}
                  mtProgramSaving={mtProgramSaving}
                  onToggleMedicalTourismProgram={toggleMedicalTourismProgram}
                  primaryDirectoryUserId={primaryUserIdStr}
                  primaryUserLoginActive={primaryUserLoginActive}
                  enableUserLoginBusy={enableUserLoginBusy}
                  onEnableUserLogin={onEnableUserLogin}
                  workflowBusy={workflowBusy}
                  onPartnerWorkflowStatusChange={onPartnerWorkflowStatusChange}
                  partnerEditLockToken={editLockToken}
                  dossierEditBlocked={dossierEditBlocked}
                />
              )}

              {activeTab === "teleconsultation" && (
                <PartnerTeleconsultationTab
                  partner={partner}
                  clinicsSummary={clinicsSummary}
                  publicWebBase={publicWebBase}
                  tcSaving={tcSaving}
                  onSavePartnerTc={savePartnerTc}
                  clinicChannelBusyId={clinicTcChannelBusyId}
                  onClinicTeleconsultationPatch={updateClinicTeleconsultationChannel}
                  onRequestAddClinic={() => hasPrimaryUser && setActiveTab("clinics")}
                />
              )}

              {activeTab === "clinics" && (
                <PartnerDetailsClinicsTab
                  partner={partner}
                  clinics={clinics}
                  clinicPage={clinicPage}
                  clinicLimit={clinicLimit}
                  totalClinics={totalClinics}
                  hasPrimaryUser={hasPrimaryUser}
                  clinicChannelBusyId={clinicTcChannelBusyId}
                  onClinicOffersPatch={patchClinicOffersAndRefreshList}
                  onConfirmUpdateClinic={confirmUpdateClinic}
                  onOpenClinicDetails={(clinic) =>
                    setShowClinicDetailsModal({ isOpen: true, clinic })
                  }
                  onFetchClinicsPage={fetchClinicsPage}
                />
              )}

              {/* Team: primary account, co-managers, practitioners, staff */}
              {activeTab === "team" && (
                <div>
                  <p className="text-soft mb-4">
                    Practitioners are aggregated across <strong>all clinics</strong> of this partner. Each row is one user linked to one
                    clinic (see the Clinic column). Co-managers are partner-level dashboard access, separate from the practitioner list.
                  </p>
                  <div className="alert alert-info mb-4" role="region" aria-label="Clinic practitioner shortcuts">
                    <strong className="d-block mb-1">Add doctors or staff (per clinic)</strong>
                    <p className="mb-2 small">
                      Co-managers only share the partner dashboard. To add someone who sees patients, use the practitioners page for that
                      clinic (invite the user first if needed).
                    </p>
                    {clinicsForPractitionerAdminLinks.length === 0 ? (
                      <p className="mb-0 small">
                        No clinics listed here yet. Use the{" "}
                        <Button
                          color="link"
                          className="p-0 align-baseline text-info"
                          type="button"
                          onClick={() => hasPrimaryUser && setActiveTab("clinics")}
                          disabled={!hasPrimaryUser}
                        >
                          Clinics
                        </Button>{" "}
                        tab to add a clinic, or open{" "}
                        <Button
                          color="link"
                          className="p-0 align-baseline text-info"
                          type="button"
                          onClick={() => history.push(getPublicUrl(`clinics?partnerId=${partner._id}`))}
                        >
                          Clinic management (this partner)
                        </Button>
                        .
                      </p>
                    ) : (
                      <div className="d-flex flex-wrap align-items-center">
                        {clinicsForPractitionerAdminLinks.map((c) => (
                          <Button
                            key={c._id}
                            color="primary"
                            size="sm"
                            outline
                            className="mr-2 mb-2"
                            type="button"
                            onClick={() => history.push(getPublicUrl(`clinics/${c._id}/practitioners`))}
                          >
                            Practitioners — {c.name || c.clinicName || "Clinic"}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                  <BlockHead size="sm" className="mb-2">
                    <BlockTitle tag="h5">Primary account (login)</BlockTitle>
                  </BlockHead>
                  {viewerIsAdmin && !hasPrimaryUser ? (
                    <div className="card card-bordered mb-4">
                      <div className="card-inner">
                        <h6 className="title mb-2">Link primary account</h6>
                        <p className="text-soft small mb-3">
                          Search the real user account (login). It cannot already be primary for another partner. After linking, partner
                          contact name and email are kept aligned with that user when you save <strong>Edit partner profile</strong>.
                        </p>
                        <Row className="g-2 align-items-end">
                          <Col md="8" lg="7">
                            <label className="form-label" htmlFor="primary-user-picker">
                              User
                            </label>
                            <UserPicker
                              idPrefix="primary-user-picker"
                              value={selectedPrimaryLinkUser}
                              onChange={setSelectedPrimaryLinkUser}
                              disabled={primaryLinkBusy}
                              filterCandidate={filterPrimaryLinkCandidate}
                              placeholder="Search by email or name…"
                              emptyHint="No users found — create the account under Users first."
                            />
                          </Col>
                          <Col md="auto" className="pt-4 mt-md-2">
                            <Button
                              color="primary"
                              type="button"
                              disabled={primaryLinkBusy || !selectedPrimaryLinkUser}
                              onClick={() => linkOrReplacePrimaryUser({ replace: false })}
                            >
                              {primaryLinkBusy ? "…" : "Link primary account"}
                            </Button>
                          </Col>
                        </Row>
                        <div className="mt-3">
                          <Button
                            color="light"
                            size="sm"
                            type="button"
                            onClick={() => history.push(getPublicUrl("user-list-compact"))}
                          >
                            Open Users list
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {viewerIsAdmin && hasPrimaryUser ? (
                    <div className="card card-bordered mb-4 border-warning">
                      <div className="card-inner">
                        <h6 className="title mb-2">Change primary account</h6>
                        <p className="text-soft small mb-3">
                          Admin only. Point this partner at another existing login. The previous primary user is unlinked from this
                          partner (role reverts to client unless they are admin). If the new primary was a co-manager, that entry is
                          removed automatically.
                        </p>
                        <Row className="g-2 align-items-end">
                          <Col md="8" lg="7">
                            <label className="form-label" htmlFor="primary-user-replace-picker">
                              New primary user
                            </label>
                            <UserPicker
                              idPrefix="primary-user-replace-picker"
                              value={selectedPrimaryLinkUser}
                              onChange={setSelectedPrimaryLinkUser}
                              disabled={primaryLinkBusy}
                              excludeUserIds={[primaryUserIdStr].filter(Boolean)}
                              filterCandidate={filterPrimaryLinkCandidate}
                              placeholder="Search by email or name…"
                              emptyHint="No eligible users found."
                            />
                          </Col>
                          <Col md="auto" className="pt-4 mt-md-2">
                            <Button
                              color="warning"
                              type="button"
                              className="text-dark"
                              disabled={primaryLinkBusy || !selectedPrimaryLinkUser}
                              onClick={() => linkOrReplacePrimaryUser({ replace: true })}
                            >
                              {primaryLinkBusy ? "…" : "Change primary"}
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  ) : null}
                  {user ? (
                    <Row className="g-gs">
                      <Col md="6">
                        <Block>
                          <BlockHead size="sm">
                            <BlockTitle tag="h5">Personal details</BlockTitle>
                          </BlockHead>
                          <div className="profile-ud-list">
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">First name</span>
                                <span className="profile-ud-value">{user.first_name || "N/A"}</span>
                              </div>
                            </div>
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">Name</span>
                                <span className="profile-ud-value">{user.last_name || "N/A"}</span>
                              </div>
                            </div>
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">Email</span>
                                <span className="profile-ud-value">{user.email || "N/A"}</span>
                              </div>
                            </div>
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">Phone</span>
                                <span className="profile-ud-value">{user.phone || "N/A"}</span>
                              </div>
                            </div>
                          </div>
                        </Block>
                      </Col>
                      <Col md="6">
                        <Block>
                          <BlockHead size="sm">
                            <BlockTitle tag="h5">Account</BlockTitle>
                          </BlockHead>
                          <div className="profile-ud-list">
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">Role</span>
                                <span className="profile-ud-value">
                                  <Badge className={`badge-dim badge-${userRoleBadgeClass(user.role)}`} pill>
                                    {user.role || "N/A"}
                                  </Badge>
                                  {!["medical_partner", "admin"].includes(user.role) && 
                                   (partner.status === "active" || partner.status === "Active") && (
                                    <Badge color="danger" className="ms-2" pill>
                                      Not synced
                                    </Badge>
                                  )}
                                  {user.role === "admin" && 
                                   (partner.status === "active" || partner.status === "Active") && (
                                    <Badge color="info" className="ms-2" pill>
                                      Admin preserved (medical partner access)
                                    </Badge>
                                  )}
                                  {user.role === "manager" && 
                                   (partner.status === "active" || partner.status === "Active") && (
                                    <Badge color="warning" className="ms-2 text-dark" pill>
                                      Expected role: medical_partner
                                    </Badge>
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">Medical Partner ID</span>
                                <span className="profile-ud-value">
                                  {user.medicalPartnerId ? (
                                    user.medicalPartnerId === partner._id || 
                                    (typeof user.medicalPartnerId === "object" && user.medicalPartnerId._id === partner._id) ||
                                    (typeof user.medicalPartnerId === "string" &&
                                      user.medicalPartnerId.toString() === partner._id.toString()) ? (
                                      <span className="text-success">
                                        <Icon name="check" className="me-1" />
                                        Linked ({partner._id})
                                      </span>
                                    ) : (
                                      <span className="text-danger">
                                        <Icon name="alert-circle" className="me-1" />
                                        Linked to another partner (
                                        {typeof user.medicalPartnerId === "object"
                                          ? user.medicalPartnerId._id
                                          : user.medicalPartnerId}
                                        )
                                        <br />
                                        <small className="text-muted">Expected partner: {partner._id}</small>
                                      </span>
                                    )
                                  ) : user.contactPartnerId ? (
                                    <span className="text-muted">
                                      <Icon name="info" className="me-1" />
                                      User document still has legacy <code>contactPartnerId</code> (historical); API routes use{" "}
                                      <code>medicalPartnerId</code> only.
                                        </span>
                                    ) : (
                                      <span className="text-warning">
                                        <Icon name="alert-triangle" className="me-1" />
                                      Not linked — user has no medicalPartnerId
                                        <br />
                                        <small className="text-muted">Partner should link to: {partner._id}</small>
                                      </span>
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">Status</span>
                                <span className="profile-ud-value">
                                  <Badge
                                    className={`badge-dim badge-${userAccountStatusBadgeVariant(user.status)}`}
                                  >
                                    {userAccountStatusLabel(user.status)}
                                  </Badge>
                                  {userAccountStatusBadgeVariant(user.status) === "danger" &&
                                  user.banReason ? (
                                    <div className="small text-soft mt-1">Reason: {user.banReason}</div>
                                  ) : null}
                                </span>
                              </div>
                            </div>
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">User ID</span>
                                <span className="profile-ud-value">{user._id || "N/A"}</span>
                              </div>
                            </div>
                            <div className="profile-ud-item">
                              <div className="profile-ud wider">
                                <span className="profile-ud-label">Created</span>
                                <span className="profile-ud-value">
                                  {user.createdAt ? moment(user.createdAt).format("DD/MM/YYYY") : "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="mt-3">
                            <Button
                              color="primary"
                              size="sm"
                              type="button"
                              outline
                              onClick={() => history.push(getPublicUrl(`user-details-regular/${user._id}`))}
                            >
                              Open full user record (admin)
                            </Button>
                          </div>
                        </Block>
                      </Col>
                    </Row>
                  ) : (
                    <div className="text-center p-5">
                      <p className="text-soft">No user information available</p>
                    </div>
                  )}

                  <BlockHead size="sm" className="mt-4 mb-2">
                    <BlockTitle tag="h5">Co-managers</BlockTitle>
                  </BlockHead>
                  <p className="text-soft small mb-2">
                    Users with shared partner access (dashboard, clinics, prices — not the same as clinic practitioners).
                    Search by email or name; the primary account cannot be added here. Users who are primary on another
                    partner are excluded unless they belong to this partner.
                  </p>
                  <Row className="g-2 align-items-end mb-3">
                    <Col md="8" lg="7">
                      <label className="form-label" htmlFor="co-manager-user-picker">
                        Add co-manager
                      </label>
                      <UserPicker
                        idPrefix="co-manager-user-picker"
                        value={selectedCoManagerUser}
                        onChange={setSelectedCoManagerUser}
                        disabled={coManagerBusy || !hasPrimaryUser}
                        excludeUserIds={[primaryUserIdStr, ...coManagerIdsNormalized()].filter(Boolean)}
                        filterCandidate={filterCoManagerCandidate}
                        placeholder="Search user by email or name…"
                        emptyHint="No users found — create the account under Users first (e.g. manager)"
                      />
                    </Col>
                    <Col md="auto" className="pt-4 mt-md-2">
                      <Button color="primary" type="button" disabled={coManagerBusy || !selectedCoManagerUser} onClick={addCoManager}>
                        {coManagerBusy ? "…" : "Add co-manager"}
                      </Button>
                    </Col>
                  </Row>
                  {Array.isArray(partner?.coManagers) && partner.coManagers.length > 0 ? (
                    <ul className="list list-sm mb-4">
                      {partner.coManagers.map((cm) => {
                        const cid = cm?._id || cm;
                        const label =
                          typeof cm === "object" && cm != null
                            ? [cm.first_name, cm.last_name].filter(Boolean).join(" ").trim() || cm.email || String(cid)
                            : String(cid);
                        return (
                          <li key={String(cid)} className="d-flex flex-wrap align-items-center justify-content-between">
                            <span className="mr-2 mb-2">
                              <strong>{label}</strong>
                              {typeof cm === "object" && cm?.email ? (
                                <span className="text-soft"> · {cm.email}</span>
                              ) : null}
                            </span>
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              className="mb-2"
                              disabled={coManagerBusy}
                              onClick={() => removeCoManager(cm)}
                            >
                              Remove
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-soft small mb-4">No co-managers configured.</p>
                  )}

                  <BlockHead size="sm" className="mb-2">
                    <BlockTitle tag="h5">Practitioners</BlockTitle>
                  </BlockHead>
                  <p className="text-soft small mb-2">
                    Medical practitioners linked to a clinic. Verify licences here; approve or reject public catalog
                    listing in{" "}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={openCatalogueGolive}
                    >
                      Catalogue go-live
                    </button>
                    .
                  </p>
                  <div className="alert alert-light border small mb-3" role="status">
                    The former <strong>TC OK</strong> toggle has been retired. Catalog visibility follows the listing
                    decision on <strong>Catalogue go-live</strong>. Licence verification below still feeds that
                    checklist.
                  </div>
                  {practitioners.filter((p) => !isStaffRole(p.role)).length === 0 ? (
                    <p className="text-soft mb-4">
                      No practitioner rows (doctors) yet. Use the clinic buttons above, then <strong>Add practitioner</strong> on that
                      screen.
                    </p>
                  ) : (
                    <DataTable className="card-stretch mb-4" scrollable>
                      <DataTableBody compact>
                        <DataTableHead>
                          <DataTableRow>
                            <span className="sub-text">Name</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Clinic</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Catalog listing</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Languages</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Country</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Licenses</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Licence document</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Diplomas</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Diploma notes</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text" title="Agent attestation after registry check">
                              Licence verified
                            </span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Role</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Email</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Link to clinic</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Actions</span>
                          </DataTableRow>
                        </DataTableHead>
                        {practitioners
                          .filter((p) => !isStaffRole(p.role))
                          .map((p) => {
                            const cid = p.clinicId || p.clinic;
                            const busy = practitionerBusyId === String(p._id);
                            const linkTargets = (clinicsSummary || []).filter((c) => {
                              if (!c?._id || String(c._id) === String(cid)) return false;
                              const uid = p.user?._id || p.user;
                              if (!uid) return false;
                              const already = practitioners.some(
                                (o) =>
                                  String(o.user?._id || o.user) === String(uid) &&
                                  String(o.clinicId || o.clinic) === String(c._id)
                              );
                              return !already;
                            });
                            return (
                              <DataTableItem key={p._id}>
                                <DataTableRow>{p.displayName || p.user?.email || p._id}</DataTableRow>
                                <DataTableRow>{p.clinicName || "—"}</DataTableRow>
                                <DataTableRow>{renderListingStatus(p)}</DataTableRow>
                                <DataTableRow>
                                  {Array.isArray(p.consultationLanguages) && p.consultationLanguages.length > 0
                                    ? p.consultationLanguages.join(", ").toUpperCase()
                                    : "—"}
                                </DataTableRow>
                                <DataTableRow>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ maxWidth: 64 }}
                                    value={practitionerFieldDrafts[String(p._id)]?.practiceCountry ?? ""}
                                    placeholder="FR"
                                    maxLength={2}
                                    disabled={busy}
                                    onChange={(e) => {
                                      const id = String(p._id);
                                      const raw = e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2);
                                      setPractitionerFieldDrafts((prev) => ({
                                        ...prev,
                                        [id]: { ...(prev[id] || {}), practiceCountry: raw },
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      const v = e.target.value.trim().toUpperCase();
                                      const prev = (p.practiceCountry || "").trim().toUpperCase();
                                      if (v === prev) return;
                                      if (v && !/^[A-Z]{2}$/.test(v)) {
                                        swal("Validation", "Country must be a 2-letter ISO code (e.g. FR).", "warning");
                                        setPractitionerFieldDrafts((d) => ({
                                          ...d,
                                          [String(p._id)]: {
                                            ...d[String(p._id)],
                                            practiceCountry: prev,
                                          },
                                        }));
                                        return;
                                      }
                                      updatePractitionerCompliance(p, { practiceCountry: v || null });
                                    }}
                                    aria-label="Practice country ISO2"
                                  />
                                </DataTableRow>
                                <DataTableRow>
                                  <LicenseEditors
                                    practiceCountry={
                                      practitionerFieldDrafts[String(p._id)]?.practiceCountry ||
                                      p.practiceCountry
                                    }
                                    licenses={p.licenses}
                                    professionalId={p.professionalId}
                                    medicalLicenceNumber={p.medicalLicenceNumber}
                                    disabled={busy}
                                    onSave={(licenses) => updatePractitionerCompliance(p, { licenses })}
                                  />
                                </DataTableRow>
                                <DataTableRow>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ minWidth: 100 }}
                                    value={practitionerFieldDrafts[String(p._id)]?.diplomas ?? ""}
                                    placeholder="Comma-separated"
                                    disabled={busy}
                                    onChange={(e) => {
                                      const id = String(p._id);
                                      setPractitionerFieldDrafts((prev) => ({
                                        ...prev,
                                        [id]: { ...(prev[id] || {}), diplomas: e.target.value },
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      const raw = e.target.value.trim();
                                      const prev = Array.isArray(p.diplomas) ? p.diplomas.join(", ") : "";
                                      if (raw === prev) return;
                                      const next = raw
                                        ? raw
                                            .split(/[,;\n]+/)
                                            .map((s) => s.trim())
                                            .filter(Boolean)
                                        : [];
                                      const same =
                                        next.length === (p.diplomas || []).length &&
                                        next.every((c, i) => c === (p.diplomas || [])[i]);
                                      if (same) return;
                                      updatePractitionerCompliance(p, { diplomas: next });
                                    }}
                                    aria-label="Diploma list"
                                  />
                                </DataTableRow>
                                <DataTableRow>
                                  <div className="d-flex flex-column align-items-start" style={{ minWidth: 112 }}>
                                    {p.licenceDocumentUrl ? (
                                      <>
                                        <a
                                          href={resolveBackendAssetUrl(p.licenceDocumentUrl)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="small mb-1"
                                        >
                                          Open file
                                        </a>
                                        <Button
                                          color="light"
                                          size="sm"
                                          className="py-0 px-2 small mb-1"
                                          disabled={busy}
                                          type="button"
                                          onClick={() => deleteLicenceDocument(p)}
                                        >
                                          Remove
                                        </Button>
                                      </>
                                    ) : (
                                      <span className="text-soft small mb-1">None</span>
                                    )}
                                    <label
                                      className={`btn btn-light btn-sm py-0 px-2 mb-0 small ${busy ? "disabled" : ""}`}
                                      style={{ cursor: busy ? "not-allowed" : "pointer" }}
                                    >
                                      <input
                                        type="file"
                                        className="d-none"
                                        disabled={busy}
                                        accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                                        onChange={(ev) => {
                                          const f = ev.target.files && ev.target.files[0];
                                          ev.target.value = "";
                                          if (f) uploadLicenceDocument(p, f);
                                        }}
                                      />
                                      Upload
                                    </label>
                                  </div>
                                </DataTableRow>
                                <DataTableRow>
                                  <textarea
                                    className="form-control form-control-sm"
                                    rows={2}
                                    style={{ minWidth: 120, resize: "vertical" }}
                                    value={practitionerFieldDrafts[String(p._id)]?.credentialDiplomas ?? ""}
                                    placeholder="Diplomas / certifications"
                                    disabled={busy}
                                    onChange={(e) => {
                                      const id = String(p._id);
                                      setPractitionerFieldDrafts((prev) => ({
                                        ...prev,
                                        [id]: { ...(prev[id] || {}), credentialDiplomas: e.target.value },
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      const v = e.target.value.trim();
                                      const prev = (p.credentialDiplomas || "").trim();
                                      if (v === prev) return;
                                      updatePractitionerCompliance(p, { credentialDiplomas: v || null });
                                    }}
                                    aria-label="Credential diplomas"
                                  />
                                </DataTableRow>
                                <DataTableRow>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={!!p.credentialsVerified}
                                    disabled={busy}
                                    onChange={(e) =>
                                      updatePractitionerCompliance(p, { credentialsVerified: e.target.checked })
                                    }
                                    aria-label="I have verified this licence"
                                    title="Check after confirming the registration number with the official registry"
                                  />
                                </DataTableRow>
                                <DataTableRow>{p.role || "doctor"}</DataTableRow>
                                <DataTableRow>{p.user?.email || "—"}</DataTableRow>
                                <DataTableRow>
                                  {linkTargets.length === 0 || !(p.user?._id || p.user) ? (
                                    <span className="text-soft">—</span>
                                  ) : (
                                    <select
                                      className="form-control form-control-sm"
                                      value={practitionerLinkSelect[String(p._id)] || ""}
                                      disabled={busy}
                                      onChange={async (e) => {
                                        const v = e.target.value;
                                        if (!v) return;
                                        const id = String(p._id);
                                        setPractitionerLinkSelect((prev) => ({ ...prev, [id]: v }));
                                        await linkPractitionerToClinic(p, v);
                                        setPractitionerLinkSelect((prev) => ({ ...prev, [id]: "" }));
                                      }}
                                      aria-label="Link practitioner to another clinic"
                                    >
                                      <option value="">Add to clinic…</option>
                                      {linkTargets.map((c) => (
                                        <option key={c._id} value={c._id}>
                                          {c.name || c._id}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </DataTableRow>
                                <DataTableRow>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    outline
                                    disabled={busy}
                                    onClick={() => removePractitionerFromClinic(p)}
                                  >
                                    Remove
                                  </Button>
                                </DataTableRow>
                              </DataTableItem>
                            );
                          })}
                      </DataTableBody>
                    </DataTable>
                  )}

                  <BlockHead size="sm" className="mb-2">
                    <BlockTitle tag="h5">Staff</BlockTitle>
                  </BlockHead>
                  <p className="text-soft small mb-2">Operational roles (nurse, assistant) per clinic.</p>
                  {practitioners.filter((p) => isStaffRole(p.role)).length === 0 ? (
                    <p className="text-soft mb-0">No staff rows for this partner.</p>
                  ) : (
                    <DataTable className="card-stretch" scrollable>
                      <DataTableBody compact>
                        <DataTableHead>
                          <DataTableRow>
                            <span className="sub-text">Name</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Clinic</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Catalog listing</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Country</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Licenses</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Role</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Email</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Link to clinic</span>
                          </DataTableRow>
                          <DataTableRow>
                            <span className="sub-text">Actions</span>
                          </DataTableRow>
                        </DataTableHead>
                        {practitioners
                          .filter((p) => isStaffRole(p.role))
                          .map((p) => {
                            const cid = p.clinicId || p.clinic;
                            const busy = practitionerBusyId === String(p._id);
                            const linkTargets = (clinicsSummary || []).filter((c) => {
                              if (!c?._id || String(c._id) === String(cid)) return false;
                              const uid = p.user?._id || p.user;
                              if (!uid) return false;
                              const already = practitioners.some(
                                (o) =>
                                  String(o.user?._id || o.user) === String(uid) &&
                                  String(o.clinicId || o.clinic) === String(c._id)
                              );
                              return !already;
                            });
                            return (
                              <DataTableItem key={p._id}>
                                <DataTableRow>{p.displayName || p.user?.email || p._id}</DataTableRow>
                                <DataTableRow>{p.clinicName || "—"}</DataTableRow>
                                <DataTableRow>{renderListingStatus(p)}</DataTableRow>
                                <DataTableRow>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ maxWidth: 64 }}
                                    value={practitionerFieldDrafts[String(p._id)]?.practiceCountry ?? ""}
                                    placeholder="FR"
                                    maxLength={2}
                                    disabled={busy}
                                    onChange={(e) => {
                                      const id = String(p._id);
                                      const raw = e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2);
                                      setPractitionerFieldDrafts((prev) => ({
                                        ...prev,
                                        [id]: { ...(prev[id] || {}), practiceCountry: raw },
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      const v = e.target.value.trim().toUpperCase();
                                      const prev = (p.practiceCountry || "").trim().toUpperCase();
                                      if (v === prev) return;
                                      if (v && !/^[A-Z]{2}$/.test(v)) {
                                        swal("Validation", "Country must be a 2-letter ISO code (e.g. FR).", "warning");
                                        setPractitionerFieldDrafts((d) => ({
                                          ...d,
                                          [String(p._id)]: {
                                            ...d[String(p._id)],
                                            practiceCountry: prev,
                                          },
                                        }));
                                        return;
                                      }
                                      updatePractitionerCompliance(p, { practiceCountry: v || null });
                                    }}
                                    aria-label="Practice country ISO2"
                                  />
                                </DataTableRow>
                                <DataTableRow>
                                  <LicenseEditors
                                    practiceCountry={
                                      practitionerFieldDrafts[String(p._id)]?.practiceCountry ||
                                      p.practiceCountry
                                    }
                                    licenses={p.licenses}
                                    professionalId={p.professionalId}
                                    medicalLicenceNumber={p.medicalLicenceNumber}
                                    disabled={busy}
                                    onSave={(licenses) => updatePractitionerCompliance(p, { licenses })}
                                  />
                                </DataTableRow>
                                <DataTableRow>{p.role || "—"}</DataTableRow>
                                <DataTableRow>{p.user?.email || "—"}</DataTableRow>
                                <DataTableRow>
                                  {linkTargets.length === 0 || !(p.user?._id || p.user) ? (
                                    <span className="text-soft">—</span>
                                  ) : (
                                    <select
                                      className="form-control form-control-sm"
                                      value={practitionerLinkSelect[String(p._id)] || ""}
                                      disabled={busy}
                                      onChange={async (e) => {
                                        const v = e.target.value;
                                        if (!v) return;
                                        const id = String(p._id);
                                        setPractitionerLinkSelect((prev) => ({ ...prev, [id]: v }));
                                        await linkPractitionerToClinic(p, v);
                                        setPractitionerLinkSelect((prev) => ({ ...prev, [id]: "" }));
                                      }}
                                      aria-label="Link staff to another clinic"
                                    >
                                      <option value="">Add to clinic…</option>
                                      {linkTargets.map((c) => (
                                        <option key={c._id} value={c._id}>
                                          {c.name || c._id}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </DataTableRow>
                                <DataTableRow>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    outline
                                    disabled={busy}
                                    onClick={() => removePractitionerFromClinic(p)}
                                  >
                                    Remove
                                  </Button>
                                </DataTableRow>
                              </DataTableItem>
                            );
                          })}
                      </DataTableBody>
                    </DataTable>
                  )}
                </div>
              )}

              {activeTab === "configuration" && (
                <div>
                  <p className="text-soft mb-3">
                    Shortcuts only — each target screen remains the source of truth.{" "}
                    <strong>This partner</strong> links always include <code className="small">partnerId={partner._id}</code> or this
                    partner&apos;s clinic IDs. <strong>Platform-wide</strong> tools are global and are not filtered by partner.
                  </p>
                  <Row className="g-gs">
                    <Col md="6">
                      <div className="card card-bordered h-100">
                        <div className="card-inner">
                          <h6 className="title">This partner</h6>
                          <p className="text-soft small">Scoped to {partner.organization_name || "this partner"} and its clinics.</p>
                          <ul className="link-list-opt">
                            <li>
                              <Button color="light" size="sm" className="w-100 text-start" onClick={() => setActiveTab("teleconsultation")}>
                                Teleconsultation (approval &amp; per-clinic TC)
                              </Button>
                            </li>
                            <li className="mt-2">
                              <Button color="light" size="sm" className="w-100 text-start" onClick={() => setActiveTab("partnerSettings")}>
                                Partner settings (HQ, timezone, language)
                              </Button>
                            </li>
                            <li className="mt-2">
                              <Button color="light" size="sm" className="w-100 text-start" onClick={() => setActiveTab("clinics")}>
                                Clinics list (status, defaults)
                              </Button>
                            </li>
                            <li className="mt-2">
                              <Button
                                color="light"
                                size="sm"
                                className="w-100 text-start"
                                onClick={() => history.push(getPublicUrl(`clinics?partnerId=${partner._id}`))}
                              >
                                Clinic management (editor, filtered by this partner)
                              </Button>
                            </li>
                          </ul>
                          <h6 className="title mt-3">Clinic deep links</h6>
                          <p className="text-soft small mb-2">
                            Treatment price grids and calendars are per clinic. Links use this partner&apos;s clinic IDs only.
                            {totalClinics > clinics.length ? (
                              <>
                                {" "}
                                Showing {clinics.length} of {totalClinics} on this page — use the Clinics tab to paginate and open others.
                              </>
                            ) : null}
                          </p>
                          {clinics.length === 0 ? (
                            <p className="text-soft small mb-0">No clinics in the current page — open the Clinics tab.</p>
                          ) : (
                            <ul className="list-unstyled small mb-0">
                              {clinics.map((c) => (
                                <li key={c._id} className="mb-2 pb-2 border-bottom">
                                  <div className="fw-medium">{c.name || c.clinicName || "Clinic"}</div>
                                  <div className="btn-group btn-group-sm mt-1 flex-wrap">
                                    <Button
                                      color="light"
                                      size="sm"
                                      type="button"
                                      onClick={() =>
                                        history.push(getPublicUrl(`clinics?partnerId=${partner._id}&openClinic=${c._id}`))
                                      }
                                    >
                                      Clinic editor
                                    </Button>
                                    <Button
                                      color="light"
                                      size="sm"
                                      type="button"
                                      onClick={() => history.push(getPublicUrl(`clinics/${c._id}/prices`))}
                                    >
                                      Treatment prices
                                    </Button>
                                    <Button
                                      color="light"
                                      size="sm"
                                      type="button"
                                      onClick={() => history.push(getPublicUrl(`clinics/${c._id}/calendar`))}
                                    >
                                      TC calendar
                                    </Button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="card card-bordered h-100">
                        <div className="card-inner">
                          <h6 className="title">Platform-wide (global admin)</h6>
                          <p className="text-soft small">
                            These screens affect <strong>all</strong> partners. They do not use <code>medicalPartnerId</code>.
                          </p>
                          <ul className="link-list-opt">
                            <li>
                              <Button
                                color="light"
                                size="sm"
                                className="w-100 text-start"
                                onClick={() => history.push(getPublicUrl("admin-settings?tab=teleconsultation"))}
                              >
                                Teleconsultation rules (Settings hub)
                              </Button>
                            </li>
                            <li className="mt-2">
                              <Button
                                color="light"
                                size="sm"
                                className="w-100 text-start"
                                onClick={() => history.push(getPublicUrl("platform-setup"))}
                              >
                                Platform setup (legacy full page)
                              </Button>
                            </li>
                            <li className="mt-2">
                              <Button
                                color="light"
                                size="sm"
                                className="w-100 text-start"
                                onClick={() => history.push(getPublicUrl("platform-catalog-roots"))}
                              >
                                Platform catalog roots (TC + treatment price tree)
                              </Button>
                            </li>
                            <li className="mt-2">
                              <Button
                                color="light"
                                size="sm"
                                className="w-100 text-start"
                                onClick={() => history.push(getPublicUrl("specialty-list"))}
                              >
                                Medical specialties (full category tree editor)
                              </Button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {activeTab === "partnerSettings" && (
                <div>
                  <div
                    className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-3 border-bottom"
                    style={{ gap: "0.5rem" }}
                  >
                    <p className="text-soft small mb-0">
                      Edit regional defaults, identity, and primary user in <strong>Full profile form</strong> (Partner + User tabs).
                    </p>
                    {viewerIsAdmin ? (
                      <Button
                        color="primary"
                        size="sm"
                        type="button"
                        disabled={dossierEditBlocked}
                        onClick={openEditProfileModal}
                      >
                        <Icon name="edit-alt-fill" className="mr-1" />
                        Full profile form
                      </Button>
                    ) : null}
                  </div>
                  {!viewerIsAdmin ? (
                    <p className="text-soft small mb-4">
                      Partner profile edits are available to admins via <strong>Full profile form</strong>.
                    </p>
                  ) : null}
                  <Row className="g-gs">
                    <Col md="6">
                      <Block>
                        <BlockHead size="sm">
                          <BlockTitle tag="h5">Operational contact (summary)</BlockTitle>
                        </BlockHead>
                        <div className="profile-ud-list">
                          <div className="profile-ud-item">
                            <div className="profile-ud wider">
                              <span className="profile-ud-label">Address</span>
                              <span className="profile-ud-value">
                                {[partner.contact_person_address, partner.contact_person_city, partner.contact_person_country]
                                  .filter(Boolean)
                                  .join(", ") || "—"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Block>
                    </Col>
                    <Col md="6">
                      <Block>
                        <BlockHead size="sm">
                          <BlockTitle tag="h5">Regional defaults (read-only)</BlockTitle>
                        </BlockHead>
                        <div className="profile-ud-list small">
                          <div className="profile-ud-item">
                            <span className="profile-ud-label">Timezone</span>
                            <span className="profile-ud-value">{partner.defaultTimezone || "—"}</span>
                          </div>
                          <div className="profile-ud-item">
                            <span className="profile-ud-label">Language</span>
                            <span className="profile-ud-value">{partner.defaultLanguage || "—"}</span>
                          </div>
                        </div>
                      </Block>
                    </Col>
                    <Col md="12">
                      <Block>
                        <BlockHead size="sm">
                          <BlockTitle tag="h5">Billing</BlockTitle>
                        </BlockHead>
                        <p className="text-soft small mb-0">
                          Open{" "}
                          <a
                            href={getPublicUrl(`invoice-list?medicalPartner=${partner._id}`)}
                            onClick={(e) => {
                              e.preventDefault();
                              history.push(getPublicUrl(`invoice-list?medicalPartner=${partner._id}`));
                            }}
                          >
                            Invoices
                          </a>{" "}
                          for documents linked to this partner.
                        </p>
                      </Block>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </Card>
        </Block>

        <EditMedicalPartnerModal
          isOpen={editProfileModalOpen}
          partnerId={partner?._id ? String(partner._id) : null}
          onClose={closeEditProfileModal}
          onSaved={onEditProfileSaved}
          editLockToken={editLockToken}
          dossierEditBlocked={dossierEditBlocked}
        />

        <ConfirmModal
          isOpen={showConfirmModal.isOpen}
          onClose={() => setShowConfirmModal({ isOpen: false, type: null, clinic: null, onConfirm: null })}
          onConfirm={showConfirmModal.onConfirm || (() => {})}
          title={
            showConfirmModal.type === "approveClinic"
              ? "Approve clinic for the platform"
              : showConfirmModal.type === "rejectClinic"
              ? "Send clinic back to draft"
              : "Confirm"
          }
          message={
            showConfirmModal.type === "approveClinic"
              ? `Mark "${showConfirmModal.clinic?.name || showConfirmModal.clinic?.clinicName || "this clinic"}" as active. Visibility for patients still depends on teleconsultation flags, catalogue, and platform approval.`
              : showConfirmModal.type === "rejectClinic"
              ? `Return "${showConfirmModal.clinic?.name || showConfirmModal.clinic?.clinicName || "this clinic"}" to draft. The partner should fix the file before you approve again.`
              : "Confirm this action?"
          }
          confirmText={
            showConfirmModal.type === "approveClinic"
              ? "Approve"
              : showConfirmModal.type === "rejectClinic"
              ? "Reject to draft"
              : "Confirm"
          }
          cancelText="Cancel"
          type={showConfirmModal.type === "approveClinic" ? "success" : showConfirmModal.type === "rejectClinic" ? "danger" : "warning"}
          loading={false}
        />

        <Modal
          isOpen={showClinicDetailsModal.isOpen}
          toggle={() => setShowClinicDetailsModal({ isOpen: false, clinic: null })}
          className="modal-dialog-centered"
          size="lg"
        >
          <ModalHeader
            toggle={() => setShowClinicDetailsModal({ isOpen: false, clinic: null })}
          >
            Clinic details
          </ModalHeader>
          <ModalBody>
            {showClinicDetailsModal.clinic ? (
              <Row className="g-gs">
                <Col md="12">
                  <Block>
                    <BlockHead size="sm">
                      <BlockTitle tag="h6">Basic information</BlockTitle>
                    </BlockHead>
                    <div className="profile-ud-list">
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Name</span>
                          <span className="profile-ud-value">
                            {showClinicDetailsModal.clinic.name || showClinicDetailsModal.clinic.clinicName || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Status</span>
                          <span className="profile-ud-value"><PartnerStatusBadge status={showClinicDetailsModal.clinic.status} labelMode="listing" /></span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Default clinic</span>
                          <span className="profile-ud-value">{showClinicDetailsModal.clinic.isDefault ? "Yes" : "No"}</span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Availability</span>
                          <span className="profile-ud-value">
                            {showClinicDetailsModal.clinic.availability !== false ? "Available" : "Unavailable"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Block>
                  <Block className="mt-4">
                    <BlockHead size="sm">
                      <BlockTitle tag="h6">Address</BlockTitle>
                    </BlockHead>
                    <div className="profile-ud-list">
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Street</span>
                          <span className="profile-ud-value">
                            {showClinicDetailsModal.clinic.address?.street ||
                              showClinicDetailsModal.clinic.address?.address ||
                              "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">City</span>
                          <span className="profile-ud-value">{showClinicDetailsModal.clinic.address?.city || "N/A"}</span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Country</span>
                          <span className="profile-ud-value">{showClinicDetailsModal.clinic.address?.country || "N/A"}</span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Postal code</span>
                          <span className="profile-ud-value">{showClinicDetailsModal.clinic.address?.zipCode || "N/A"}</span>
                        </div>
                      </div>
                    </div>
                  </Block>
                  <Block className="mt-4">
                    <BlockHead size="sm">
                      <BlockTitle tag="h6">Contact</BlockTitle>
                    </BlockHead>
                    <div className="profile-ud-list">
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Phone</span>
                          <span className="profile-ud-value">{showClinicDetailsModal.clinic.phoneNumber || "N/A"}</span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Email</span>
                          <span className="profile-ud-value">{showClinicDetailsModal.clinic.email || "N/A"}</span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Website</span>
                          <span className="profile-ud-value">
                            {showClinicDetailsModal.clinic.website ? (
                              <a href={showClinicDetailsModal.clinic.website} target="_blank" rel="noopener noreferrer">
                                {showClinicDetailsModal.clinic.website}
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Currency</span>
                          <span className="profile-ud-value">{showClinicDetailsModal.clinic.defaultCurrency || "USD"}</span>
                        </div>
                      </div>
                    </div>
                  </Block>
                  <Block className="mt-4">
                    <BlockHead size="sm">
                      <BlockTitle tag="h6">Additional information</BlockTitle>
                    </BlockHead>
                    <div className="profile-ud-list">
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Created</span>
                          <span className="profile-ud-value">
                            {showClinicDetailsModal.clinic.createdAt
                              ? moment(showClinicDetailsModal.clinic.createdAt).format("DD/MM/YYYY")
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="profile-ud-item">
                        <div className="profile-ud wider">
                          <span className="profile-ud-label">Photos</span>
                          <span className="profile-ud-value">
                            {showClinicDetailsModal.clinic.photos && Array.isArray(showClinicDetailsModal.clinic.photos)
                              ? `${showClinicDetailsModal.clinic.photos.length} photo(s)`
                              : "0 photos"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Block>
                </Col>
              </Row>
            ) : (
              <div className="text-center p-4">
                <p className="text-soft">Loading details...</p>
              </div>
            )}
          </ModalBody>
          {showClinicDetailsModal.clinic ? (
            <ModalFooter className="justify-content-between">
              <Button
                color="light"
                type="button"
                onClick={() =>
                  history.push(
                    getPublicUrl(`clinics?partnerId=${partner._id}&openClinic=${showClinicDetailsModal.clinic._id}`)
                  )
                }
              >
                <Icon name="home" className="me-1" />
                Open in Clinic management
              </Button>
              <div>
                <Button
                  color="primary"
                  type="button"
                  className="me-2"
                  onClick={() => {
                    const id = showClinicDetailsModal.clinic._id;
                    setShowClinicDetailsModal({ isOpen: false, clinic: null });
                    history.push(getPublicUrl(`clinics/${id}/prices`));
                  }}
                >
                  <Icon name="calc" className="me-1" />
                  Manage prices
                </Button>
                <Button color="light" type="button" onClick={() => setShowClinicDetailsModal({ isOpen: false, clinic: null })}>
                  Close
                </Button>
              </div>
            </ModalFooter>
          ) : null}
        </Modal>
    </>
  ) : null;

  return (
    <React.Fragment>
      {!inPanel ? <Head title="Medical Partner Details" /> : null}
      {loading ? (
        inPanel ? (
          <div className="partner-detail-panel-loading h-100">{loadingInner}</div>
        ) : (
          <Content>{loadingInner}</Content>
        )
      ) : !partner ? (
        inPanel ? (
          <div className="h-100">{notFoundInner}</div>
        ) : (
          <Content>{notFoundInner}</Content>
        )
      ) : inPanel ? (
        <div
          className="partner-detail-panel-body h-100 overflow-auto"
          style={{ maxHeight: "calc(100vh - 160px)", minHeight: 360 }}
        >
          {layoutBody}
        </div>
      ) : (
        <Content>{layoutBody}</Content>
      )}
    </React.Fragment>
  );
}

export default MedicalPartnerDetails;

